// Server state the andon module's own screens read — the raise flow, the
// thread, the Andon tab's floor list and the operator home's rows. Kept apart
// from `queries/index.ts`, which is the shop floor's own contract with its own
// BFF routes: these go to `/api/andon/*`, answer 404 while the module is
// dormant, and are only ever called with the preview on.
//
// TWO PREFIXES, ON PURPOSE.
//
// Everything that is ABOUT ANDONS sits under 'andons': home, one alert's
// thread, the floor list. The live stream nudges that one prefix when an alert
// changes (see `useAndonLive`), the raise and every verb invalidate it, and
// the shop floor's own andon lists in `queries/index.ts` share it — so one
// invalidation reaches whichever screen happens to be mounted.
//
// Reference data — the cause taxonomy, the @ roster, the responder list, the
// station list, a GIF search — sits under 'andonRef' and is left alone by the
// stream. A nudge about alert 900 says nothing about who is on the roster, and
// re-reading seventy cause classes on every message would be the traffic the
// stream exists to avoid.

import { useQuery } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import type { AlertTypeKey } from '@/api/andon/types';
import { useLiveInterval } from '@/hooks/useAndonLive';

export const andonKeys = {
  home: ['andons', 'home'] as const,
  /** One andon's thread. Under the live prefix: a nudge names exactly this. */
  alert: (id: number) => ['andons', 'alert', id] as const,
  /** Every open andon on the floor, or every one plus the last day's settled. */
  floor: (scope: 'open' | 'all') => ['andons', 'floor', scope] as const,
  routingPreview: (type: AlertTypeKey, workcentreId: number | null) => ['andons', 'routingPreview', type, workcentreId] as const,
  /** Shared lists the browser holds across raises — see `Question`. */
  subjects: ['andons', 'subjects'] as const,
  defects: ['andons', 'defects'] as const,
  causes: ['andonRef', 'causes'] as const,
  roster: ['andonRef', 'roster'] as const,
  responders: ['andonRef', 'responders'] as const,
  stations: ['andonRef', 'stations'] as const,
  gifs: (term: string) => ['andonRef', 'gifs', term] as const,
};

/**
 * The operator's home payload: the station the session holds, round 0's
 * constants with their chips, and which tiles this session may raise.
 *
 * The raise flow OWNS this query. The Andon app read it off its home screen's
 * cache, which was on screen while the operator picked a severity; the shop
 * floor has no such screen, so the flow asks for it the moment it opens and
 * the tile grid is one tap later than the fetch — long enough on a factory
 * network, and the grid draws its fallback set rather than waiting if not.
 *
 * Thirty seconds stale: the chips change when a pod arrives at the station,
 * not between two taps.
 */
export function useAndonHome(enabled: boolean) {
  return useQuery({
    queryKey: andonKeys.home,
    queryFn: () => andonApi.home(),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Who this raise will actually reach.
 *
 * Re-asked whenever the type or the station changes, because both change the
 * answer: tier 1 resolves through the station's department and the specialist
 * comes off the type. The server resolves it through the same function the
 * raise itself calls a second later, so what is shown cannot drift from what
 * happens.
 */
export function useRoutingPreview(type: AlertTypeKey | null, workcentreId: number | null) {
  return useQuery({
    queryKey: andonKeys.routingPreview(type ?? 'other', workcentreId),
    queryFn: () => andonApi.routingPreview(type!, workcentreId),
    enabled: type != null,
    staleTime: 60_000,
  });
}

/**
 * One andon, in full: the record, its thread and its events.
 *
 * The stream pushes messages and state changes as they happen, so the poll is
 * the backstop behind it rather than the way things arrive: a second BFF
 * instance, a reconnect that missed something, a phone that slept. Five
 * seconds while the stream is down, twenty while it is up.
 */
export function useAlertDetail(alertId: number | null) {
  const interval = useLiveInterval(5_000, 20_000);
  return useQuery({
    queryKey: andonKeys.alert(alertId ?? 0),
    queryFn: () => andonApi.alert(alertId!),
    enabled: alertId != null,
    refetchInterval: interval,
  });
}

/**
 * The floor: every open andon, or every one plus the last day's settled.
 *
 * One read for the leader's Andon tab and the operator's home rows, filtered
 * in the browser — a hundred cards at the worst of it, and a station's few
 * are a filter over them rather than a query of their own. Polled at the
 * same rate as the shop floor's own andon lists.
 */
export function useAndonFloor(scope: 'open' | 'all', enabled: boolean) {
  const interval = useLiveInterval(20_000, 60_000);
  return useQuery({
    queryKey: andonKeys.floor(scope),
    queryFn: () => andonApi.floor(scope),
    enabled,
    refetchInterval: interval,
  });
}

/**
 * The cause taxonomy. Cached hard: around seventy rows that change only when
 * somebody edits the config, and the close sheet has to be answerable the
 * instant it opens.
 */
export function useCauses(enabled: boolean) {
  return useQuery({ queryKey: andonKeys.causes, queryFn: () => andonApi.causes(), enabled, staleTime: 600_000 });
}

/** Who can be named with an @. Changes on the timescale of somebody being hired. */
export function useMentionRoster() {
  return useQuery({ queryKey: andonKeys.roster, queryFn: () => andonApi.mentionRoster(), staleTime: 300_000 });
}

/** Who an andon can be handed to — a different list from the roster. */
export function useResponders(enabled: boolean) {
  return useQuery({ queryKey: andonKeys.responders, queryFn: () => andonApi.responders(), enabled, staleTime: 300_000 });
}

/** Every work centre, for the close sheet's "where was it caused" picker. */
export function useAndonStations(enabled: boolean) {
  return useQuery({ queryKey: andonKeys.stations, queryFn: () => andonApi.stations(), enabled, staleTime: 600_000 });
}
